/**
 * SignalR Notification Types
 * Based on backend SignalR hub implementation
 */

import { NotificationActionType } from '@ata-digital/client-tax-system-api-client';

export enum NotificationSeverity {
  Info = 'Info',
  Success = 'Success',
  Warning = 'Warning',
  Error = 'Error',
}

export enum OperationTypes {
  BINDER_CREATION = 'binder_creation',
  DOCUMENT_UPLOAD = 'document_upload',
  CCH_CLIENT_IMPORT = 'cch_client_import',
  PDF_PROCESSING = 'pdf_processing',
  ENGAGEMENT_LETTER_EXTRACTION = 'engagement_letter_extraction',
}

/**
 * Progress Operation Types
 * Used for real-time progress tracking via SignalR ReceiveProgressUpdate events
 * Source: Backend SignalR progress reporting (ProgressOperationTypes)
 */
export const ProgressOperationTypes = {
  BINDER_CREATION: 'binder_creation',
  BATCH_UPLOAD: 'batch_upload',
  DOCUMENT_UPLOAD: 'document_upload',
  CCH_CLIENT_IMPORT: 'cch_client_import',
  PDF_PROCESSING: 'pdf_processing',
  ENGAGEMENT_LETTER_EXTRACTION: 'engagement_letter_extraction',
  SIGNATURE_WORKFLOW: 'signature_workflow',
  BINDER_EXPORT: 'binder_export',
  QUESTIONNAIRE_PROCESSING: 'questionnaire_processing',
  AUDIT_BINDER_IMPORT: 'audit_binder_import',
  AUDIT_BINDER_ZIP_IMPORT: 'audit_binder_zip_import',
} as const;

export type ProgressOperationType = (typeof ProgressOperationTypes)[keyof typeof ProgressOperationTypes];

// Base notification interface
export interface NotificationDto {
  type: NotificationActionType | null;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  timestamp: string;
  severity: NotificationSeverity;
  requiresAction?: boolean;
  actionUrl?: string;
  actionText?: string;
}

// Progress notification for long-running operations
export interface ProgressNotificationDto extends NotificationDto {
  operationId: string;
  operationType: string;
  progress: number; // 0-100
  currentStep: string;
  totalSteps?: number;
  currentStepNumber?: number;
  isCompleted: boolean;
  hasError: boolean;
  errorMessage?: string;
  processedItems?: string[];
}

// Progress update (from backend ReceiveProgressUpdate hub method)
// Sent during long-running operations with explicit operation type
export interface ProgressUpdateDto {
  operationId: string;
  operationType: ProgressOperationType; // Explicit operation type from backend
  progress: number; // 0-100
  currentStep: string;
  currentStepNumber?: number;
  totalSteps?: number;
  timestamp: string;
}

// Union type for all notification types
export type SignalRNotification = NotificationDto | ProgressNotificationDto;

export interface ZipImportItemDto {
  operationId: string;
  kind: 'request' | 'file' | 'summary';
  status: 'created' | 'saved' | 'skipped' | 'failed' | 'done';
  label: string;
  parentPath?: string;
  detail?: string;
}
